import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    Stack
} from '@mui/material';
import { PictureAsPdf as PictureAsPdfIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import inventarioInformes from '../../services/inventarioInformes';
import { TIPOS_MOVIMIENTO_STOCK } from '../../constants/tiposMovimientoStock';
import InformesBackButton from '../../components/shared/InformesBackButton';
import PDFViewer from '../../components/shared/PDFViewer';
import SearchableSelect from '../../components/SearchableSelect';

const TIPO_MOVIMIENTO_STOCK_OPTIONS = Object.entries(TIPOS_MOVIMIENTO_STOCK).map(([value, name]) => {
    const prefix = value.includes('-') ? value.split('-')[0] : value;
    return { value, label: `${prefix} - ${name}` };
});

/** Filtros iniciales; si vienen codigoArticulo/deposito por URL (p. ej. desde Stock por depósito), amplía fechas a un año para ver historial. */
function buildInitialMovimientosFilters(searchParams: URLSearchParams) {
    const codigoArticulo = searchParams.get('codigoArticulo') ?? '';
    const deposito = searchParams.get('deposito') ?? '';
    const today = new Date();
    const fechaHasta = today.toISOString().split('T')[0];
    let fechaDesde = fechaHasta;
    if (codigoArticulo.trim() !== '' || deposito.trim() !== '') {
        const d = new Date(today);
        d.setFullYear(d.getFullYear() - 1);
        fechaDesde = d.toISOString().split('T')[0];
    }
    return {
        fechaDesde,
        fechaHasta,
        codigoArticulo,
        tipoMovimiento: '',
        deposito,
    };
}

interface MovimientoStock {
    nroInterno: number;
    idformulario: number;
    codArticulo: string;
    descripcion: string;
    tipoMov: string;
    descripcionMov?: string;
    cantidad: number;
    fechaMov: Date;
    deposito: number;
    nroSuc: number;
    precioCosto: number;
    letra: string;
    nroComprobante: number;
}

interface Ubicacion {
    nroUbicacion: number;
    nombreUbicacion: string;
}


type TipoMovimiento = keyof typeof TIPOS_MOVIMIENTO_STOCK;

const getNombreTipoMovimiento = (mov: MovimientoStock): string => {
    if (mov.descripcionMov) return mov.descripcionMov;
    const key = Object.keys(TIPOS_MOVIMIENTO_STOCK).find(k => 
        k === mov.tipoMov || mov.tipoMov?.startsWith(k)
    ) as TipoMovimiento | undefined;
    return key ? TIPOS_MOVIMIENTO_STOCK[key] : mov.tipoMov || '-';
};

const MovimientosStock: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(false);
    const [movimientos, setMovimientos] = useState<MovimientoStock[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [ubicaciones, setUbicaciones] = useState<Ubicacion[]>([]);
    const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
    const previewPdfUrlRef = useRef<string | null>(null);
    previewPdfUrlRef.current = previewPdfUrl;

    const [filters, setFilters] = useState(() => buildInitialMovimientosFilters(searchParams));

    useEffect(() => {
        return () => {
            if (previewPdfUrlRef.current) URL.revokeObjectURL(previewPdfUrlRef.current);
        };
    }, []);

    const handleClosePdfPreview = () => {
        setPreviewPdfUrl((prev) => {
            if (prev) URL.revokeObjectURL(prev);
            return null;
        });
    };

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await inventarioInformes.getMovimientosStock({
                fechaDesde: filters.fechaDesde ? new Date(filters.fechaDesde) : undefined,
                fechaHasta: filters.fechaHasta ? new Date(filters.fechaHasta) : undefined,
                codigoArticulo: filters.codigoArticulo || undefined,
                tipoMovimiento: filters.tipoMovimiento || undefined,
                deposito: filters.deposito ? parseInt(filters.deposito) : undefined,
                page: page + 1,
                pageSize: rowsPerPage
            });

            setMovimientos(response.data ?? []);
            const count = response.totalCount;
            setTotalCount(typeof count === 'number' && count >= 0 ? count : 0);
        } catch (error: unknown) {
            console.error('Error al cargar movimientos:', error);
            const err = error as { response?: { data?: { error?: string } } };
            const message = err.response?.data?.error || 'Error al cargar los movimientos de stock';
            enqueueSnackbar(message, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    }, [filters, page, rowsPerPage, enqueueSnackbar]);

    // Cargar datos al montar el componente
    useEffect(() => {
        loadData();
    }, [loadData]);

    // Agregar useEffect para cargar las ubicaciones al montar el componente
    useEffect(() => {
        const loadUbicaciones = async () => {
            try {
                const data = await inventarioInformes.getUbicaciones();
                setUbicaciones(data);
            } catch (error) {
                console.error('Error al cargar ubicaciones:', error);
                enqueueSnackbar('Error al cargar las ubicaciones', { variant: 'error' });
            }
        };
        loadUbicaciones();
    }, [enqueueSnackbar]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement | { name?: string; value: unknown }>) => {
        const { name, value } = event.target;
        setFilters(prev => ({
            ...prev,
            [name as string]: value
        }));
    };

    const handleSearch = () => {
        setPage(0);
        loadData();
    };

    const handleExportPdf = async () => {
        try {
            setLoading(true);
            const blob = await inventarioInformes.getMovimientosStockPdf({
                fechaDesde: filters.fechaDesde ? new Date(filters.fechaDesde) : undefined,
                fechaHasta: filters.fechaHasta ? new Date(filters.fechaHasta) : undefined,
                codigoArticulo: filters.codigoArticulo || undefined,
                tipoMovimiento: filters.tipoMovimiento || undefined,
                deposito: filters.deposito ? parseInt(filters.deposito) : undefined
            });
            
            if (blob.size === 0) {
                enqueueSnackbar('No hay datos para generar el PDF', { variant: 'warning' });
                return;
            }

            const pdfBlob =
                blob instanceof Blob && blob.type === 'application/pdf'
                    ? blob
                    : new Blob([blob], { type: 'application/pdf' });
            const url = URL.createObjectURL(pdfBlob);
            setPreviewPdfUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return url;
            });
        } catch (error: any) {
            console.error('Error al generar PDF:', error);
            const errorMessage = error.response?.data?.error || 'Error al generar el PDF';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
            <Card
                sx={{
                    bgcolor: '#ffffff',
                    borderRadius: 3,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{ mb: 3 }}
                    >
                        <InformesBackButton to="/inventario/informes" useBrowserBack />
                        <Typography
                            variant="h4"
                            sx={{ fontWeight: 600, color: 'primary.main' }}
                        >
                            Movimientos de Stock
                        </Typography>
                    </Stack>

                    <Card sx={{ mb: 3, bgcolor: '#fafafa' }}>
                        <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                label="Fecha Desde"
                                type="date"
                                name="fechaDesde"
                                value={filters.fechaDesde}
                                onChange={handleFilterChange}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                label="Fecha Hasta"
                                type="date"
                                name="fechaHasta"
                                value={filters.fechaHasta}
                                onChange={handleFilterChange}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <TextField
                                fullWidth
                                label="Código Artículo"
                                name="codigoArticulo"
                                value={filters.codigoArticulo}
                                onChange={handleFilterChange}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <SearchableSelect
                                label="Tipo Movimiento"
                                size="small"
                                allowEmpty
                                emptyOptionLabel="Todos"
                                options={TIPO_MOVIMIENTO_STOCK_OPTIONS}
                                value={filters.tipoMovimiento || null}
                                onChange={(value) => setFilters((prev) => ({ ...prev, tipoMovimiento: value ?? '' }))}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <SearchableSelect
                                label="Depósito"
                                size="small"
                                allowEmpty
                                emptyOptionLabel="Todos"
                                options={ubicaciones.map((ubicacion) => ({
                                    value: String(ubicacion.nroUbicacion),
                                    label: `${ubicacion.nroUbicacion} - ${ubicacion.nombreUbicacion}`,
                                }))}
                                value={filters.deposito || null}
                                onChange={(value) => setFilters((prev) => ({ ...prev, deposito: value ?? '' }))}
                            />
                        </Grid>
                    </Grid>
                    <Box mt={2} display="flex" gap={2}>
                        <Button
                            variant="contained"
                            onClick={handleSearch}
                            disabled={loading}
                        >
                            Buscar
                        </Button>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={handleExportPdf}
                            disabled={loading}
                            sx={{
                                borderRadius: 2,
                                px: { xs: 2, sm: 3 },
                                py: 1,
                                textTransform: 'none',
                                fontWeight: 500,
                            }}
                        >
                            Generar PDF
                        </Button>
                    </Box>
                        </CardContent>
                    </Card>

                    <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Fecha</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Comprobante</TableCell>
                            <TableCell>Código</TableCell>
                            <TableCell>Descripción</TableCell>
                            <TableCell>Depósito</TableCell>
                            <TableCell align="right">Cantidad</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {movimientos.map((movimiento) => (
                            <TableRow key={movimiento.nroInterno}>
                                <TableCell>{new Date(movimiento.fechaMov).toLocaleDateString()}</TableCell>
                                <TableCell>{getNombreTipoMovimiento(movimiento)}</TableCell>
                                <TableCell>
                                    {movimiento.letra && movimiento.nroSuc != null && movimiento.nroComprobante != null 
                                        ? `${movimiento.letra}-${movimiento.nroSuc.toString().padStart(4, '0')}-${movimiento.nroComprobante.toString().padStart(8, '0')}`
                                        : '-'}
                                </TableCell>
                                <TableCell>{movimiento.codArticulo}</TableCell>
                                <TableCell>{movimiento.descripcion}</TableCell>
                                <TableCell>{movimiento.deposito}</TableCell>
                                <TableCell align="right">{movimiento.cantidad}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    labelRowsPerPage="Filas por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
            </TableContainer>
                </CardContent>
            </Card>

            {previewPdfUrl ? (
                <PDFViewer
                    pdfUrl={previewPdfUrl}
                    onClose={handleClosePdfPreview}
                    title="Movimientos de stock"
                    documentId="movimientos-stock"
                    documentType="informeCliente"
                    downloadFileName="MovimientosStock.pdf"
                />
            ) : null}
        </Box>
    );
};

export default MovimientosStock;
